import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Calculator, Send, Sparkles, ClipboardCheck } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import { AIPanel, AIButton, AIExplanationBox } from '../components/AIComponents';

const mockEstimate = {
  id: 101,
  title: 'Office Renovation',
  client: { name: 'New Client A', email: 'client@company.com' },
  status: 'draft',
  date: '2024-01-15',
  sections: [
    {
      title: 'Demolition & Prep',
      items: [
        { description: 'Remove existing partitions', cost: 1500 },
        { description: 'Dispose of waste materials', cost: 800 },
      ]
    },
    {
      title: 'Construction',
      items: [
        { description: 'New partition walls', cost: 4500 },
        { description: 'Ceiling grid installation', cost: 2200 },
        { description: 'Flooring', cost: 3500 },
      ]
    },
    {
      title: 'Electrical',
      items: [
        { description: 'New wiring and sockets', cost: 1800 },
        { description: 'Lighting installation', cost: 1200 },
      ]
    }
  ],
  totalLabour: 8500,
  totalMaterials: 5000,
  contingency: 1500,
  total: 15000
};

export function EstimateDetail() {
  const { id } = useParams();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 'var(--space-xl)', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Main Content */}
      <div className="detail-page" style={{ maxWidth: 'none' }}>
        <Link to="/quotes/estimates" className="back-link"><ArrowLeft size={16} />Back to Estimates</Link>

        <div className="detail-header">
          <div className="detail-title-row">
            <div>
              <h1 className="detail-title">Estimate #{id}</h1>
              <p className="detail-subtitle">{mockEstimate.title} • {mockEstimate.client.name}</p>
            </div>
            <span className={`status-badge ${mockEstimate.status === 'converted' ? 'completed' : mockEstimate.status === 'sent' ? 'pending' : 'draft'}`} style={{ padding: '8px 16px', fontSize: '0.75rem' }}>{mockEstimate.status}</span>
          </div>
        </div>

        {/* Estimate Breakdown */}
        {mockEstimate.sections.map((section, sIndex) => (
          <GlassPanel key={sIndex} title={section.title} titleIcon={Calculator} className="mb-lg">
            <div className="list-container">
              {section.items.map((item, iIndex) => (
                <div key={iIndex} className="list-item" style={{ cursor: 'default' }}>
                  <span style={{ color: 'var(--text-primary)' }}>{item.description}</span>
                  <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>£{item.cost.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </GlassPanel>
        ))}

        {/* Summary */}
        <GlassPanel title="Summary" className="mb-xl">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>Labour</span>
              <span style={{ color: 'var(--text-primary)' }}>£{mockEstimate.totalLabour.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>Materials</span>
              <span style={{ color: 'var(--text-primary)' }}>£{mockEstimate.totalMaterials.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>Contingency (10%)</span>
              <span style={{ color: 'var(--text-primary)' }}>£{mockEstimate.contingency.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--glass-border)', marginTop: 'var(--space-md)' }}>
              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Total Estimate</span>
              <span style={{ fontWeight: '700', fontSize: '1.5rem', color: 'var(--accent-primary)' }}>£{mockEstimate.total.toLocaleString()}</span>
            </div>
          </div>
        </GlassPanel>

        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          <button className="btn btn-primary" disabled style={{ flex: 1 }}><Send size={16} />Convert to Quote</button>
          <button className="btn btn-secondary" disabled style={{ flex: 1 }}><FileText size={16} />Export PDF</button>
        </div>
      </div>

      {/* AI Actions Side Panel */}
      <div className="ai-side-panel">
        <AIPanel 
          title="AI Actions" 
          description="AI can help validate your estimate and convert it to a professional quote."
        >
          <div className="ai-actions-grid single-col">
            <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
              Convert estimate to quote (AI assisted)
            </AIButton>
            <AIButton icon={Calculator} variant="full" tooltip="AI generation will be enabled soon">
              Check pricing realism
            </AIButton>
            <AIButton icon={ClipboardCheck} variant="full" tooltip="AI generation will be enabled soon">
              Suggest missing items
            </AIButton>
          </div>
        </AIPanel>

        <AIExplanationBox>
          AI compares your estimate against historical data and market rates to ensure competitive pricing.
        </AIExplanationBox>
      </div>
    </div>
  );
}

export default EstimateDetail;
